import { useRef } from 'react';
import type { MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet } from 'react-router-dom';

import Footer from './Footer';
import Header from './Header';
import VisibilityBar from './VisibilityBar';
import type { LayoutVariant } from './layoutTypes';
import { useLayoutNavigation } from './useLayoutNavigation';

const MAIN_ID = 'main-content';

interface MainLayoutProps {
    variant: LayoutVariant;
}

/**
 * Layout de l'application, utilisé comme route parente (la page s'affiche dans <Outlet />).
 *
 * - "standard" : barre de visibilité, header collé en haut, contenu dans le flux, footer en bas.
 * - "map" : le contenu (la carte) occupe tout l'écran ; barre, header et footer flottent
 *   par-dessus. Leurs conteneurs laissent passer les clics (pointer-events-none), seuls
 *   les éléments visibles les captent. Le contenu doit remplir son parent (h-full w-full).
 */
export default function MainLayout({ variant }: MainLayoutProps) {
    const { t } = useTranslation();
    const { mainNav, footerSections } = useLayoutNavigation();
    const mainRef = useRef<HTMLElement>(null);

    // Lien d'évitement : un lien "#ancre" modifierait l'URL du routeur, on déplace le focus.
    const skipToContent = (event: MouseEvent<HTMLAnchorElement>) => {
        event.preventDefault();
        mainRef.current?.focus();
    };

    const skipLink = (
        <a
            href={`#${MAIN_ID}`}
            onClick={skipToContent}
            className="sr-only focus:not-sr-only focus:fixed focus:left-3 focus:top-3 focus:z-50 focus:rounded focus:bg-gray-900 focus:px-4 focus:py-2 focus:text-sm focus:text-white dark:focus:bg-white dark:focus:text-gray-900"
        >
            {t('layout.skipToContent')}
        </a>
    );

    if (variant === 'map') {
        return (
            <div className="relative h-dvh overflow-hidden">
                {skipLink}
                {/* z-20 : au-dessus de la carte, dont les contrôles internes sont isolés (isolate). */}
                <div className="pointer-events-none absolute inset-x-0 top-0 z-20 flex flex-col">
                    <VisibilityBar />
                    <Header variant="map" navItems={mainNav} />
                </div>
                <main
                    id={MAIN_ID}
                    ref={mainRef}
                    tabIndex={-1}
                    className="absolute inset-0 isolate outline-none"
                >
                    <Outlet />
                </main>
                <div className="pointer-events-none absolute inset-x-0 bottom-0 z-20">
                    <Footer variant="map" />
                </div>
            </div>
        );
    }

    return (
        <div className="flex min-h-dvh flex-col">
            {skipLink}
            <VisibilityBar />
            <Header variant="standard" navItems={mainNav} />
            <main id={MAIN_ID} ref={mainRef} tabIndex={-1} className="flex-1 outline-none">
                <Outlet />
            </main>
            <Footer variant="standard" sections={footerSections} />
        </div>
    );
}
